import React, { useRef } from "react";
import useAnchoredPopover from "../../../hooks/useAnchoredPopover";
import "./DrawingToolbarMenu.sass";

/**
 * Button of the drawing toolbar with a popover under it (🎨 and ≡). Escape closes
 * the popover only: the drawing stays selected.
 *
 * @param {object}   props
 * @param {string}   props.label - accessible name of the button and the popover
 * @param {React.ReactNode} props.icon
 * @param {React.ReactNode} props.marker - the current value in the corner of the button
 * @param {boolean}  props.isOpen
 * @param {() => void} props.onToggle
 * @param {() => void} props.onClose
 * @param {React.ReactNode} props.children - content of the popover
 */
const DrawingToolbarMenu = ({ label, icon, marker, isOpen, onToggle, onClose, children }) => {
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverStyle = useAnchoredPopover({ anchorRef, popoverRef, isOpen, onClose });

  const handleKeyDown = (event) => {
    if (event.key !== "Escape" || !isOpen) return;

    event.stopPropagation();
    onClose();
    anchorRef.current?.focus();
  };

  return (
    <span className="drawing-toolbar-menu" onKeyDown={handleKeyDown}>
      <button
        ref={anchorRef}
        type="button"
        className={`drawing-toolbar-menu__trigger${isOpen ? " drawing-toolbar-menu__trigger--open" : ""}`}
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        {icon}
        {marker}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          className="drawing-toolbar-menu__popover"
          style={popoverStyle}
          role="dialog"
          aria-label={label}
        >
          {children}
        </div>
      )}
    </span>
  );
};

export default DrawingToolbarMenu;
